import React from "react";
import { Calendar, CalendarX2, FileText, MessageSquare, CheckCircle, Clock, AlertCircle, Send, Eye, Paperclip, Repeat } from "lucide-react";
import type { UserTask } from "../../../types/task";

interface UserTaskCardProps {
  task: UserTask;
  onSubmit?: (task: UserTask) => void;
  onViewSubmission?: (task: UserTask) => void;
  viewMode?: "list" | "grid";
  canSubmit?: boolean;
}

export const UserTaskCard: React.FC<UserTaskCardProps> = ({
  task,
  onSubmit,
  onViewSubmission,
  viewMode = "list",
  canSubmit: canSubmitProp = false,
}) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("tr-TR", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadge = () => {
    if (task.submission) {
      const status = task.submission.status;
      if (status === "APPROVED") {
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-green-500 to-green-600 text-white shadow-sm">
            <CheckCircle className="w-3 h-3" />
            Onaylandı
          </span>
        );
      }
      if (status === "REJECTED") {
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-red-500 to-red-600 text-white shadow-sm">
            <AlertCircle className="w-3 h-3" />
            Reddedildi
          </span>
        );
      }
      return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-sm">
          <CheckCircle className="w-3 h-3" />
          Gönderildi
        </span>
      );
    }

    const statusInfo = task.statusInfo;
    if (!statusInfo || !statusInfo.label) {
      return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-gray-400 to-gray-500 text-white shadow-sm">
          <AlertCircle className="w-3 h-3" />
          Bilinmiyor
        </span>
      );
    }

    const label = statusInfo.label;
    const daysText = statusInfo.daysRemaining !== null ? ` (${statusInfo.daysRemaining} Gün)` : "";

    if (label === "Tamamlandı") {
      return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-teal-500 to-teal-600 text-white shadow-sm">
          <CheckCircle className="w-3 h-3" />
          {label}
        </span>
      );
    }

    if (label === "Aktif") {
      return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-indigo-500 to-indigo-600 text-white shadow-sm">
          <Clock className="w-3 h-3" />
          {label}
          {daysText}
        </span>
      );
    }

    if (label === "Acil") {
      return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-sm">
          <Clock className="w-3 h-3 animate-pulse" />
          {label}
          {daysText}
        </span>
      );
    }

    if (label === "Süresi Doldu" || label === "Atanmamış" || label === "Kullanıcı Yok") {
      return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-rose-500 to-rose-600 text-white shadow-sm">
          <AlertCircle className="w-3 h-3" />
          {label}
        </span>
      );
    }

    if (label === "Bilinmiyor") {
      return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-gray-400 to-gray-500 text-white shadow-sm">
          <AlertCircle className="w-3 h-3" />
          {label}
        </span>
      );
    }

    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-sky-500 to-sky-600 text-white shadow-sm">
        <Clock className="w-3 h-3" />
        {label}
        {daysText}
      </span>
    );
  };

  const getItemTypeIcon = () => {
    if (task.itemType === "FILE") {
      return <FileText className="w-4 h-4 text-green-600" />;
    }
    if (task.itemType === "BOTH") {
      return <Paperclip className="w-4 h-4 text-orange-600" />;
    }
    return <MessageSquare className="w-4 h-4 text-blue-600" />;
  };

  const getItemTypeBgColor = () => {
    if (task.itemType === "FILE") {
      return "bg-green-100";
    }
    if (task.itemType === "BOTH") {
      return "bg-orange-100";
    }
    return "bg-blue-100";
  };

  const getFrequencyText = () => {
    const frequencyMap = {
      ONCE: "Bir Kez",
      DAILY: "Günlük",
      WEEKLY: "Haftalık",
      MONTHLY: "Aylık",
    };
    return frequencyMap[task.frequency] || "Bir Kez";
  };

  const canSubmitTask = () => {
    if (!canSubmitProp) return false;

    const now = new Date();
    const endDate = new Date(task.endDate);

    return task.itemType === "TEXT" && !task.hasSubmitted && now <= endDate && task.status === "ACTIVE";
  };

  if (viewMode === "grid") {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow flex flex-col h-full">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3 flex-1 min-w-0">
            <div className="flex-shrink-0">
              <div className={`h-10 w-10 ${getItemTypeBgColor()} rounded-full flex items-center justify-center`}>{getItemTypeIcon()}</div>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-gray-900 mb-1 break-words">{task.title}</h3>
              <div className="flex items-center gap-2">
                <span className="text-xs text-purple-600 font-semibold">{getFrequencyText()}</span>
              </div>
            </div>
          </div>
        </div>

        <p className="text-sm text-gray-600 mb-4 line-clamp-3 leading-relaxed">{task.description}</p>

        <div className="mb-4 space-y-2">
          <div className="text-sm">
            <div className="flex items-center gap-2 text-gray-700 font-medium mb-1">
              <Calendar className="h-4 w-4 text-green-600" />
              <span>Başlangıç:</span>
            </div>
            <div className="text-gray-600 ml-6">{formatDate(task.startDate)}</div>
          </div>
          <div className="text-sm">
            <div className="flex items-center gap-2 text-gray-700 font-medium mb-1">
              <CalendarX2 className="h-4 w-4 text-red-600" />
              <span>Bitiş:</span>
            </div>
            <div className="text-gray-600 ml-6">{formatDate(task.endDate)}</div>
          </div>
        </div>

        <div className="mb-4">{getStatusBadge()}</div>

        {task.submission?.reviewNote && (
          <div
            className={`mb-4 p-3 border rounded-md ${task.submission.status === "REJECTED" ? "bg-red-50 border-red-200" : "bg-blue-50 border-blue-200"
              }`}>
            <p className={`text-xs font-medium mb-1 ${task.submission.status === "REJECTED" ? "text-red-900" : "text-blue-900"}`}>
              {task.submission.status === "REJECTED" ? "⚠️ Reddedildi - Geri Bildirim:" : "Geri Bildirim:"}
            </p>
            <p className={`text-sm ${task.submission.status === "REJECTED" ? "text-red-800" : "text-blue-800"}`}>
              {task.submission.reviewNote}
            </p>
          </div>
        )}

        <div className="mt-auto pt-4 border-t border-gray-100">
          {task.creator && (
            <div className="mb-4">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center overflow-hidden">
                  {task.creator.media?.url ? (
                    <img
                      src={task.creator.media.url}
                      alt={`${task.creator.firstName} ${task.creator.lastName}`}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-white text-[10px] font-medium">
                      {(task.creator.firstName || "").charAt(0)}
                      {(task.creator.lastName || "").charAt(0)}
                    </span>
                  )}
                </div>
                <div className="min-w-0">
                  <div className="text-xs text-gray-700 font-medium truncate">
                    {`${task.creator.firstName} ${task.creator.lastName}`.trim() || "Bilinmiyor"}
                  </div>
                </div>
              </div>
            </div>
          )}

          {task.submission && task.submission.status === "REJECTED" ? (
            <div className="flex flex-col items-center gap-2">
              <button
                onClick={() => onViewSubmission && onViewSubmission(task)}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium text-orange-600 bg-orange-50 hover:bg-orange-100 transition-colors">
                <Eye className="w-4 h-4" />
                Reddedilen Gönderimi Görüntüle
              </button>
              {canSubmitProp && onSubmit && (
                <button
                  onClick={() => onSubmit(task)}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 transition-colors">
                  <Send className="w-4 h-4" />
                  Gönder
                </button>
              )}
            </div>
          ) : task.hasSubmitted && onViewSubmission ? (
            <button
              onClick={() => onViewSubmission(task)}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 transition-colors">
              <Eye className="w-4 h-4" />
              Gönderimi Görüntüle
            </button>
          ) : canSubmitProp && onSubmit ? (
            <div className="flex flex-col items-center gap-2">
              {!canSubmitTask() && (
                <p className="text-center text-xs font-semibold text-red-600">
                  {task.status === "COMPLETED" || task.status === "EXPIRED"
                    ? "Görev Süresi Doldu"
                    : task.itemType !== "TEXT"
                      ? "Dosya Yükleme Geçici Olarak Kapalı"
                      : "Görev Kapalı"}
                </p>
              )}
              <button
                onClick={() => canSubmitTask() && onSubmit(task)}
                disabled={!canSubmitTask()}
                className={`w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-colors ${canSubmitTask() ? "text-white bg-blue-600 hover:bg-blue-700" : "text-gray-400 bg-gray-100 cursor-not-allowed"
                  }`}>
                <Send className="w-4 h-4" />
                Gönder
              </button>
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-2 sm:p-3 md:p-4 hover:shadow-md transition-shadow mb-2 min-w-0 overflow-hidden">
      <div className="flex items-start gap-3 min-w-0">
        <div className="flex-shrink-0">
          <div className={`h-8 w-8 ${getItemTypeBgColor()} rounded-full flex items-center justify-center`}>{getItemTypeIcon()}</div>
        </div>

        <div className="min-w-0 flex-1">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 break-words">{task.title}</h3>
          {task.description ? (
            <p className="mt-1 text-xs sm:text-sm text-gray-600 line-clamp-2 break-words">{task.description}</p>
          ) : null}

          <div className="mt-3 flex flex-col gap-2 text-xs sm:text-sm text-gray-700 min-w-0">
            <div className="flex flex-col gap-1 text-gray-600 min-w-0">
              <div className="flex items-start gap-2 min-w-0">
                <Calendar className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="break-words">{formatDate(task.startDate)}</span>
              </div>
              <div className="flex items-start gap-2 min-w-0">
                <CalendarX2 className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                <span className="break-words">{formatDate(task.endDate)}</span>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 min-w-0">
              <div className="flex items-center gap-2">
                <Repeat className="h-4 w-4 text-purple-600 flex-shrink-0" />
                <span className="font-medium">{getFrequencyText()}</span>
              </div>

              {task.creator ? (
                <div className="flex items-center gap-2 min-w-0 max-w-full">
                  <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {task.creator.media?.url ? (
                      <img
                        src={task.creator.media.url}
                        alt={`${task.creator.firstName} ${task.creator.lastName}`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-white text-[10px] font-medium">
                        {(task.creator.firstName || "").charAt(0)}
                        {(task.creator.lastName || "").charAt(0)}
                      </span>
                    )}
                  </div>
                  <span className="text-gray-700 font-medium truncate min-w-0">
                    {`${task.creator.firstName} ${task.creator.lastName}`.trim() || "Bilinmiyor"}
                  </span>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <div className="flex flex-col items-end justify-between self-stretch flex-shrink-0">
          <div className="flex flex-col items-end gap-2">
            {getStatusBadge()}
          </div>

          {task.submission && task.submission.status === "REJECTED" ? (
            <div className="flex flex-col items-end gap-1 mt-2">
              <button
                onClick={() => onViewSubmission && onViewSubmission(task)}
                className="inline-flex items-center justify-center gap-1.5 px-2 sm:px-3 py-1.5 sm:py-2 rounded-md text-xs font-medium text-orange-600 bg-orange-50 hover:bg-orange-100 transition-colors whitespace-nowrap"
                title="Gönderimi Görüntüle">
                <Eye className="w-3.5 h-3.5" />
                <span className="hidden md:inline">Görüntüle</span>
              </button>
              {canSubmitProp && onSubmit && (
                <button
                  onClick={() => onSubmit(task)}
                  className="inline-flex items-center justify-center gap-1.5 px-2 sm:px-3 py-1.5 sm:py-2 rounded-md text-xs font-medium text-white bg-red-600 hover:bg-red-700 transition-colors whitespace-nowrap"
                  title="Gönder">
                  <Send className="w-3.5 h-3.5" />
                  <span className="hidden md:inline">Gönder</span>
                </button>
              )}
            </div>
          ) : task.hasSubmitted && onViewSubmission ? (
            <button
              onClick={() => onViewSubmission(task)}
              className="inline-flex items-center justify-center gap-1.5 px-2 sm:px-3 py-1.5 sm:py-2 rounded-md text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 transition-colors whitespace-nowrap"
              title="Gönderimi Görüntüle">
              <Eye className="w-3.5 h-3.5" />
              <span className="hidden md:inline">Görüntüle</span>
            </button>
          ) : canSubmitProp && onSubmit ? (
            <div className="flex flex-col items-end gap-1 mt-2">
              <button
                onClick={() => canSubmitTask() && onSubmit(task)}
                disabled={!canSubmitTask()}
                className={`inline-flex items-center justify-center gap-1.5 px-2 sm:px-3 py-1.5 sm:py-2 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${canSubmitTask() ? "text-white bg-blue-600 hover:bg-blue-700" : "text-gray-400 bg-gray-100 cursor-not-allowed"
                  }`}
                title="Gönder">
                <Send className="w-3.5 h-3.5" />
                <span className="hidden md:inline">Gönder</span>
              </button>
              {!canSubmitTask() && (
                <p className="text-right text-[11px] font-semibold text-red-600 max-w-[10rem]">
                  {task.status === "COMPLETED" || task.status === "EXPIRED"
                    ? "Görev Süresi Doldu"
                    : task.itemType !== "TEXT"
                      ? "Dosya Yükleme Kapalı"
                      : "Görev Kapalı"}
                </p>
              )}
            </div>
          ) : null}
        </div>
      </div>

      {task.submission?.reviewNote && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <div
            className={`p-3 border rounded-md ${task.submission.status === "REJECTED" ? "bg-red-50 border-red-200" : "bg-blue-50 border-blue-200"
              }`}>
            <p className={`text-xs font-medium mb-1 ${task.submission.status === "REJECTED" ? "text-red-900" : "text-blue-900"}`}>
              {task.submission.status === "REJECTED" ? "⚠️ Reddedildi - Geri Bildirim:" : "Geri Bildirim:"}
            </p>
            <p className={`text-sm ${task.submission.status === "REJECTED" ? "text-red-800" : "text-blue-800"}`}>
              {task.submission.reviewNote}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};